import { StyleSheet, css } from 'aphrodite';
import { type FC, useEffect, useState } from 'react';

import { CoffeeLeadIssueCategory } from '../../../extViewmodels';
import { Button } from '../../../web/components-v2/Button';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../web/components-v2/Dialog';
import { TextArea } from '../../../web/components-v2/TextArea';
import { useReportLeadIssue } from '../data';

const CATEGORIES: Array<{ value: CoffeeLeadIssueCategory; label: string }> = [
	{ value: CoffeeLeadIssueCategory.WrongLocationOrTimeZone, label: 'Wrong Location/Timezone' },
	{ value: CoffeeLeadIssueCategory.Duplicate, label: 'Duplicate' },
	{ value: CoffeeLeadIssueCategory.CurrentClientNeedsAttention, label: 'Current Client Needs Attention' },
	{ value: CoffeeLeadIssueCategory.Other, label: 'Other' },
];

const MAX_DESCRIPTION = 1000;

export type ReportIssueDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	companyId: string | undefined;
	companyName?: string;
};

export const ReportIssueDialog: FC<ReportIssueDialogProps> = ({ open, onOpenChange, companyId, companyName }) => {
	const [category, setCategory] = useState<CoffeeLeadIssueCategory | null>(null);
	const [description, setDescription] = useState('');
	const reportIssue = useReportLeadIssue();

	useEffect(() => {
		if (!open) {
			setCategory(null);
			setDescription('');
		}
	}, [open]);

	const canSubmit = !!companyId && !!category && description.trim().length > 0 && !reportIssue.isPending;

	const handleSubmit = async () => {
		if (!canSubmit) return;
		try {
			await reportIssue.mutateAsync({
				category: category as CoffeeLeadIssueCategory,
				companyId: companyId as string,
				description: description.trim(),
			});
			onOpenChange(false);
		} catch {
			// useReportLeadIssue surfaces the reason; keep the dialog open so the rep can retry.
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogPopup styles={[styles.popup]}>
				<DialogHeader>
					<DialogTitle>{`Report an issue with ${companyName || 'this lead'}`}</DialogTitle>
				</DialogHeader>
				<DialogBody styles={[styles.body]}>
					<div className={css(styles.sectionLabel)}>Category</div>
					<div className={css(styles.categories)}>
						{CATEGORIES.map(option => (
							<Button
								key={option.value}
								type='button'
								variant={category === option.value ? 'secondary' : 'outline'}
								size='sm'
								styles={[styles.categoryBtn]}
								aria-pressed={category === option.value}
								onClick={() => setCategory(option.value)}
							>
								{option.label}
							</Button>
						))}
					</div>

					<label className={css(styles.sectionLabel)} htmlFor='report-issue-description'>
						Description
					</label>
					<TextArea
						id='report-issue-description'
						rows={5}
						maxLength={MAX_DESCRIPTION}
						placeholder='Describe the issue…'
						value={description}
						onChange={e => setDescription(e.target.value)}
					/>
					<div className={css(styles.charCount)}>
						{description.length}/{MAX_DESCRIPTION}
					</div>
				</DialogBody>
				<DialogFooter>
					<Button type='button' variant='outline' onClick={() => onOpenChange(false)}>
						Cancel
					</Button>
					<Button type='button' variant='primary' disabled={!canSubmit} onClick={() => void handleSubmit()}>
						{reportIssue.isPending ? 'Submitting…' : 'Submit'}
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};

const styles = StyleSheet.create({
	popup: {
		maxWidth: 520,
	},
	body: {
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
	},
	sectionLabel: {
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: '0.08em',
		textTransform: 'uppercase',
		color: 'var(--ink-faint)',
		marginTop: 6,
	},
	categories: {
		display: 'flex',
		flexWrap: 'wrap',
		gap: 6,
		marginBottom: 6,
	},
	categoryBtn: {
		flexShrink: 0,
	},
	charCount: {
		alignSelf: 'flex-end',
		fontSize: 11,
		color: 'var(--ink-faint)',
	},
});
